import React, { useContext, useState } from 'react';
import { Modal, Flex, Typography, Input, Switch, message, Button, Divider } from 'antd';
import { IUserFile } from '@leedpulse-monorepo/interfaces';
import updateUserFile from '../../../../domain/use_cases/users/updateUserFile';
import AuthenticationContext from '../../../../presentation/contexts/auth/AuthenticationContext';
import UserDoc from '../../../../domain/models/UserDoc';
import moment from 'moment';
import { CloseOutlined, DownloadOutlined, SaveOutlined } from '@ant-design/icons';

interface UserFileDetailsModalProps {
    userFile: IUserFile | undefined;
    onCancel: () => void;
    onUpdateCallback: () => void;
    userDoc: UserDoc;
    maxDescriptionLength?: number;
    downloadingZip: boolean;
    onDownloadZip: (userFile: IUserFile) => void;
}

export default function UserFileDetailsModal({
    userFile,
    onCancel,
    onUpdateCallback,
    userDoc,
    maxDescriptionLength = 50,
    downloadingZip,
    onDownloadZip
}: UserFileDetailsModalProps) {
    const authContext = useContext(AuthenticationContext);

    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editSensitive, setEditSensitive] = useState(false);
    const [savingEdit, setSavingEdit] = useState(false);

    const handleSaveEdit = async () => {
        setSavingEdit(true);
        try {
            if (!userFile) throw new Error('Fichier non trouvé');

            await updateUserFile(
                userDoc.uid,
                userFile.id,
                { name: editName.trim(), description: editDescription, sensitive: editSensitive },
                authContext.authentication
            );
            message.success('Fichier mis à jour avec succès');
            onUpdateCallback();
        } catch (e) {
            message.error(e instanceof Error ? e.message : 'Erreur lors de la mise à jour');
        } finally {
            setSavingEdit(false);
        }
    };

    // Ajuste pendant le rendu (pattern « ajuster l'etat quand une prop change » de
    // la doc React) : dans un effet, ce recalcul coutait un second rendu.
    const [lastUserFile, setLastUserFile] = useState(userFile);
    if (userFile !== lastUserFile) {
        setLastUserFile(userFile);
        if (userFile) {
            setEditName(userFile.name);
            setEditDescription(userFile.description ?? '');
            setEditSensitive(userFile.sensitive ?? false);
        }
    }

    return <Modal
        title="Détails du fichier"
        open={!!userFile}
        onOk={handleSaveEdit}
        onCancel={onCancel}
        okText="Enregistrer"
        cancelText="Annuler"
        confirmLoading={savingEdit}
        destroyOnHidden
        footer={
            <>
                <Divider />
                <Flex gap="small" justify="space-between">
                    {userFile &&
                        <Button
                            icon={<DownloadOutlined />}
                            loading={downloadingZip}
                            onClick={() => onDownloadZip(userFile)}
                        >
                            Télécharger
                        </Button>
                    }

                    <Flex gap="small">
                        <Button
                            icon={<CloseOutlined />}
                            onClick={onCancel}
                        >
                            Annuler
                        </Button>

                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={savingEdit}
                            onClick={handleSaveEdit}
                        >
                            Enregistrer
                        </Button>
                    </Flex>
                </Flex>
            </>
        }
    >
        <Flex vertical gap="middle">
            <div>
                <Typography.Text type="secondary">ID</Typography.Text>
                <div><Typography.Text copyable>{userFile?.id}</Typography.Text></div>
            </div>
            <div>
                <Typography.Text type="secondary">Date</Typography.Text>
                <div>{moment(userFile?.timestamp).format('DD/MM/YYYY à HH:mm')}</div>
            </div>
            <div>
                <Typography.Text type="secondary">Nom du fichier</Typography.Text>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div>
                <Typography.Text type="secondary">Description</Typography.Text>
                <Input
                    maxLength={maxDescriptionLength}
                    showCount
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                />
            </div>
            <Flex align="center" gap="small">
                <Switch checked={editSensitive} onChange={setEditSensitive} />
                <Typography.Text>Sensible</Typography.Text>
            </Flex>
        </Flex>
    </Modal>;
}